// Thin wrapper around the entradart backend kiosk API. Each method posts/
// gets the matching path under {backendBaseUrl}/api/kiosk/* with the
// terminal token in the X-Kiosk-Token header. Response shapes are validated
// at the network boundary by their kiosk-shared zod schema.

import {
  type CancelOrderRequest,
  type CancelOrderResponse,
  type ConfirmOrderRequest,
  type CreateOrderRequest,
  type CreateOrderResponse,
  cancelOrderResponseSchema,
  createOrderResponseSchema,
  type HandshakeRequest,
  type HandshakeResponse,
  type HeartbeatRequest,
  type HeartbeatResponse,
  handshakeResponseSchema,
  heartbeatResponseSchema,
  KIOSK_TOKEN_HEADER,
  type KioskEventsRequest,
  type KioskEventsResponse,
  kioskEventsResponseSchema,
  type MuseumConfigResponse,
  museumConfigResponseSchema,
  type PrintPayloadResponse,
  printPayloadResponseSchema,
  type TicketTypesResponse,
  ticketTypesResponseSchema,
} from "@entradart/kiosk-shared/api";

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

const TRAILING_SLASH_RE = /\/$/;

// Upper bound for the best-effort calls (telemetry, decline cancel) whose
// callers never await them on the visitor's path: without it a stalled POST
// would pin EventLog's in-flight slot forever. Purchase calls get no timeout.
export const TELEMETRY_TIMEOUT_MS = 10_000;

export type ApiClientOptions = {
  backendBaseUrl: string;
  terminalToken: string;
  fetchImpl?: typeof fetch;
};

export class ApiClient {
  private readonly base: string;
  private readonly token: string;
  private readonly fetchImpl: typeof fetch;

  constructor(opts: ApiClientOptions) {
    this.base = opts.backendBaseUrl.replace(TRAILING_SLASH_RE, "");
    this.token = opts.terminalToken;
    // Bind to the global: `this.fetchImpl(...)` would otherwise invoke the
    // native fetch with the ApiClient instance as receiver, which Chromium
    // (incl. the Android WebView) rejects with "Illegal invocation". An
    // injected test mock is a plain function and doesn't care about `this`.
    this.fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  handshake(body: HandshakeRequest): Promise<HandshakeResponse> {
    return this.post("/api/kiosk/handshake", body, handshakeResponseSchema);
  }

  museumConfig(): Promise<MuseumConfigResponse> {
    return this.get("/api/kiosk/museum-config", museumConfigResponseSchema);
  }

  ticketTypes(date: string): Promise<TicketTypesResponse> {
    return this.get(
      `/api/kiosk/ticket-types?date=${encodeURIComponent(date)}`,
      ticketTypesResponseSchema
    );
  }

  createOrder(body: CreateOrderRequest): Promise<CreateOrderResponse> {
    return this.post("/api/kiosk/orders", body, createOrderResponseSchema);
  }

  confirmOrder(
    orderId: number,
    body: ConfirmOrderRequest
  ): Promise<PrintPayloadResponse> {
    return this.post(
      `/api/kiosk/orders/${orderId}/confirm`,
      body,
      printPayloadResponseSchema
    );
  }

  printPayload(orderId: number): Promise<PrintPayloadResponse> {
    return this.get(
      `/api/kiosk/orders/${orderId}/print-payload`,
      printPayloadResponseSchema
    );
  }

  cancelOrder(
    orderId: number,
    body: CancelOrderRequest
  ): Promise<CancelOrderResponse> {
    return this.post(
      `/api/kiosk/orders/${orderId}/cancel`,
      body,
      cancelOrderResponseSchema,
      TELEMETRY_TIMEOUT_MS
    );
  }

  heartbeat(body: HeartbeatRequest): Promise<HeartbeatResponse> {
    return this.post("/api/kiosk/heartbeat", body, heartbeatResponseSchema);
  }

  postEvents(body: KioskEventsRequest): Promise<KioskEventsResponse> {
    return this.post(
      "/api/kiosk/events",
      body,
      kioskEventsResponseSchema,
      TELEMETRY_TIMEOUT_MS
    );
  }

  private async get<T>(
    path: string,
    schema: { parse: (input: unknown) => T }
  ): Promise<T> {
    const response = await this.fetchImpl(`${this.base}${path}`, {
      method: "GET",
      headers: this.headers(),
    });
    return await this.handleResponse(response, schema);
  }

  private post<T>(
    path: string,
    body: unknown,
    schema: { parse: (input: unknown) => T },
    timeoutMs?: number
  ): Promise<T> {
    if (timeoutMs === undefined) {
      return this.send(path, body, schema);
    }
    // AbortController + setTimeout rather than AbortSignal.timeout(), which
    // older Android WebViews lack. The race also covers a fetch that ignores
    // the signal, and the body read after the headers arrive.
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        controller.abort();
        reject(new ApiError(`Timed out after ${timeoutMs} ms: ${path}`, 0));
      }, timeoutMs);
    });
    return Promise.race([
      this.send(path, body, schema, controller.signal),
      timeout,
    ]).finally(() => {
      clearTimeout(timer);
    });
  }

  private async send<T>(
    path: string,
    body: unknown,
    schema: { parse: (input: unknown) => T },
    signal?: AbortSignal
  ): Promise<T> {
    const init: RequestInit = {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify(body),
    };
    if (signal) {
      init.signal = signal;
    }
    const response = await this.fetchImpl(`${this.base}${path}`, init);
    return await this.handleResponse(response, schema);
  }

  private headers(): Record<string, string> {
    return {
      "Content-Type": "application/json",
      [KIOSK_TOKEN_HEADER]: this.token,
    };
  }

  private async handleResponse<T>(
    response: Response,
    schema: { parse: (input: unknown) => T }
  ): Promise<T> {
    if (!response.ok) {
      const text = await response.text();
      throw new ApiError(
        `Backend ${response.status}: ${text || response.statusText}`,
        response.status
      );
    }
    const json = (await response.json()) as unknown;
    return schema.parse(json);
  }
}
